import React, { useCallback, useState } from "react";

import { Button, Dropdown, Modal, Stack } from "react-bootstrap";

import { compact, uniq } from "lodash";
import { useResetRecoilState } from "recoil";

import { CreditNoteReasons, useCreateCreditNoteMutation } from "@api/graphql";
import { useClearError, useHandleErrorMessage } from "@context/error";
import { ResalesType } from "@states/resale_submission";
import { selectedResales } from "@states/resale_submission";
import { formatValueToDisplayFriendly } from "@util/index";

interface ConfirmIssueCreditNotesModalProps {
  isOpen: boolean;
  resaleData: ResalesType[];
  closeModal: () => void;
  refetch?: () => void;
}

const CREDIT_NOTE_REASONS: CreditNoteReasons[] = [
  CreditNoteReasons.DUPLICATE_OR_INCORRECT_REPORTING,
  CreditNoteReasons.FINANCE_CORRECTION,
  CreditNoteReasons.RESALE_PRIOR_TO_JUNE_2010,
  CreditNoteReasons.RIGHTS_HOLDER_OPT_OUT,
];

export const ConfirmIssueCreditNotesModal = ({
  isOpen,
  closeModal,
  resaleData,
  refetch,
}: ConfirmIssueCreditNotesModalProps) => {
  const handleCloseModal = useCallback(() => closeModal(), [closeModal]);
  const [selectedReason, setSelectedReason] = useState<CreditNoteReasons | "Select a reason">("Select a reason");
  const [isConfirming, setIsConfirming] = useState<boolean>(false);

  const resetSelectedResales = useResetRecoilState(selectedResales);

  // API

  const [createCreditNote] = useCreateCreditNoteMutation();
  const handleError = useHandleErrorMessage();
  const clearError = useClearError();
  // End of API

  const handleManualCloseModal = useCallback(() => {
    setIsConfirming(false);
    setSelectedReason("Select a reason");
    handleCloseModal();
  }, [handleCloseModal]);

  const handleDropdownSelect = useCallback((eventKey: string | null) => {
    eventKey && setSelectedReason(eventKey as CreditNoteReasons);
  }, []);

  const handleApplyReason = useCallback(() => {
    setIsConfirming(true);
  }, []);

  const handleConfirmation = useCallback(async () => {
    const selectedInvoiceLines = compact(resaleData.map((resale) => resale.invoice?.invoice_line));
    const uniqueInvoices = uniq(compact(resaleData.map((item) => item.invoice?.invoice)));

    if (!uniqueInvoices || uniqueInvoices.length === 0) {
      handleError("No invoices found.");
    } else if (uniqueInvoices.length > 1) {
      handleError("More than 1 invoice found.");
    } else {
      const invoice = uniqueInvoices[0];

      const response = await createCreditNote({
        variables: {
          input: { invoice_id: invoice, lines_to_credit_note: selectedInvoiceLines, reason: selectedReason },
        },
      });

      if (!response.data?.createCreditNote?.id) {
        handleError("Failed to create credit note");
      } else {
        clearError();
        // Try to use refetchQueries after API implemented
        refetch && refetch();
        resetSelectedResales();
        handleCloseModal();
      }
    }
  }, [
    clearError,
    createCreditNote,
    handleCloseModal,
    handleError,
    refetch,
    resaleData,
    resetSelectedResales,
    selectedReason,
  ]);

  return (
    <Modal show={isOpen} centered size="lg" onHide={handleManualCloseModal} backdrop={"static"}>
      <Modal.Header closeButton />
      <Modal.Body className="p-5">
        {isConfirming ? (
          <Stack gap={5}>
            <span className="h3 align-self-center">Please confirm you want to proceed</span>
            <Stack>
              <span className="align-self-center">You cannot undo a credit note.</span>
              <span className="align-self-center">Please confirm you want to proceed</span>
            </Stack>
            <Button onClick={handleConfirmation} className="align-self-center">
              Confirm
            </Button>
          </Stack>
        ) : (
          <Stack gap={5}>
            <span className="h3 align-self-center">Select credit note reason</span>
            <Dropdown className="customized-dropdown" onSelect={handleDropdownSelect}>
              <Dropdown.Toggle variant="none" className={"customized-dropdown-toggle w-100 text-truncate"}>
                {formatValueToDisplayFriendly(selectedReason)}
              </Dropdown.Toggle>

              <Dropdown.Menu>
                {CREDIT_NOTE_REASONS.map((reason) => (
                  <Dropdown.Item eventKey={reason} key={reason} className="d-flex align-items-center">
                    {formatValueToDisplayFriendly(reason)}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            <Button
              onClick={handleApplyReason}
              className="align-self-center"
              disabled={!selectedReason || selectedReason === "Select a reason"}
            >
              Apply to {resaleData?.length || 0} reports
            </Button>
          </Stack>
        )}
      </Modal.Body>
    </Modal>
  );
};
